/** React libraries */
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";

/** Custom components types, services */
import Docupload from "../../../../components/Upload/Upload";
import { RequestMetadaContext } from "./../../logoRequest.Type";
import { DocumentUploadConstants, DocumentUploadedData, documentUploadService, DownloadDocumentResponse } from "./../../documentUploadService";
import { useLoader } from "../../../../context/LoaderContext";

/**  Style */
import "./style.css";

const DocumentUpload = () => {
  const requestMetadaContext = useContext(RequestMetadaContext);
  const { setValue, watch, register, getValues } = useFormContext();
  const { setLoading } = useLoader();
  const [filesInfo, setFilesInfo] = useState<DocumentUploadedData>();
  const [isFolderPath, setIsFolderPath] = useState<boolean>(true);
  const [isDocSuccess, setIsDocSuccess] = useState<boolean>(false);
  const [isDocFail, setIsDocFail] = useState<boolean>(false);

  const folderPath = watch("customFolderPath");

  useEffect(() => {
    fetchUploadedFile()
  }, [])

  /**
   * Fetch upload files
   */
  const fetchUploadedFile = async () => {
    try {
      let folderPath = getValues("customFolderPath");
      if (folderPath && folderPath != "") {
        await documentUploadService.fetchAllUploadedDocuments(folderPath)
          .then(response => {
            if (response) {
              setFilesInfo(response);
            } else {
              let documentUploadedData: DocumentUploadedData = { files: [] }
              setFilesInfo(documentUploadedData);
            }
          })
          .catch(error => {
            let documentUploadedData: DocumentUploadedData = { files: [] }
            setFilesInfo(documentUploadedData);
          })
      }
    } catch (exe) {
      let documentUploadedData: DocumentUploadedData = { files: [] }
      setFilesInfo(documentUploadedData);
    }
  };

  /**
   * On file upload
   * @param files 
   * @returns 
   */
  const onFileUpload = async (files: File[]) => {
    try {
      if (isFolderPath) {
        const validFileTypes = DocumentUploadConstants.DOCUMENT_FILE_TYPE_SUPPORT;
        const validFiles = files.filter((file) => validFileTypes.includes(file.type));
        if (validFiles.length !== files.length) {
          toast.error(DocumentUploadConstants.FILE_TYPE_NOT_SUPPORT_MSG);
          setIsDocFail(true);
          setTimeout(() => {
            setIsDocFail(false);
          }, 4000)
          return;
        } else {
          setLoading(true);
          let folderPath = getValues("customFolderPath");

          // Validate is path present or not if not the fetch logo request
          if (!folderPath) {
            toast.error(DocumentUploadConstants.UPLOAD_DOCUMENTS_FAIL_MSG);
            setLoading(false);
            return;
          }

          for (const file of files) {
            const timestamp = Date.now();
            const fName = file.name.split(".");
            const ext = file.name.split('.').pop();
            const newFileName = `${fName[0]}_${timestamp}.${ext}`;

            // Create a new file with the same content but different name
            const renamedFile = new File([file], newFileName, {
              type: file.type,
              lastModified: file.lastModified,
            });

            await documentUploadService.uploadFile(folderPath, renamedFile)
              .then(response => {
                setLoading(false);
                setIsDocSuccess(true);
                setFilesInfo((prev) => {
                  const updatedFiles = prev?.files ? [...prev.files, response] : [response];
                  return { files: updatedFiles };
                });
                setTimeout(() => {
                  setIsDocSuccess(false);
                }, 4000);
              })
              .catch(error => {
                setLoading(false);
                setIsDocFail(true);
                setTimeout(() => {
                  setIsDocFail(false);
                }, 4000)
              });
          }
        }
      } else {
        toast.error(DocumentUploadConstants.DOCUMET_FILEPATH_NOT_PRESENT);
      }
    } catch (exe) {
      setLoading(false);
      toast.error(DocumentUploadConstants.UPLOAD_DOCUMENTS_FAIL_MSG);
    }
  };

  /**
   * On file download
   * @param filename 
   */
  const onFileDownload = async (filename: string) => {
    try {
      setLoading(true);
      let folderPath = getValues("customFolderPath");
      let filePath = folderPath + "/" + filename;
      let response: DownloadDocumentResponse = await documentUploadService.downloadDocument(filePath);
      if (response && response.fileBlob) {
        const url = window.URL.createObjectURL(response.fileBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      }
      setLoading(false);
    } catch (exe) {
      setLoading(false);
      toast.error(DocumentUploadConstants.DOWNLOAD_DOCUMENTS_FAIL_MSG);
    }
  }

  return (
    <>
      {requestMetadaContext.isDocumentUploadEnabled && (!folderPath || folderPath === "") ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '390px' }}>
          Loading...
        </div>
      ) : (
        <Docupload
          filesInfo={filesInfo}
          visible={false}
          disabled={!requestMetadaContext.isDocumentUploadEnabled}
          sidebyside={false}
          isFolderPath={isFolderPath}
          isDocSuccess={isDocSuccess}
          isDocFail={isDocFail}
          supportedFileTypes={DocumentUploadConstants.DOCUMENT_FILE_TYPE_SUPPORT}
          infoMessage="Please attach a proof file showing the FSC label as it will appear in the final publication, along with any other FSC references (e.g., in the document text) in a PDF file"
          onFileUpload={onFileUpload}
          onFileDownload={onFileDownload}
        />
      )}
    </>
  )
}

export default DocumentUpload